import { useMemo, useState } from "react";
import {
  Card,
  Button,
  Select,
  Space,
  Tag,
  message,
  Collapse,
  Table,
  Empty,
  Popconfirm,
  Checkbox,
  Modal,
  Form,
  DatePicker,
  Input,
  Tooltip,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useGetStaffMemberQuery,
  useGetStaffDeployedWeeksQuery,
  useGetStaffDeployedWeekQuery,
  useBulkDeployWeeksMutation,
  useDeleteStaffDeployedWeekMutation,
  useGetMachineGroupsQuery,
  useGetStaffVacationsQuery,
  useCreateStaffVacationMutation,
  useUpdateStaffVacationMutation,
  useDeleteStaffVacationMutation,
  useGetStaffOvertimesQuery,
  useCreateStaffOvertimeMutation,
  useUpdateStaffOvertimeMutation,
  useDeleteStaffOvertimeMutation,
  useExcludeShiftFromDeployedWeekMutation,
  useGetShiftDayTemplatesQuery,
  useSetDayOverrideMutation,
  useClearDayOverrideMutation,
} from "../../../redux/api/scheduling";
import { toFacilityUTC, formatDateTime } from "../_shared/utils/dateUtils";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LABELS_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function getSunday(d: dayjs.Dayjs) {
  return d.subtract(d.day(), "day");
}

interface Props {
  memberId: number;
}

export default function StaffCalendar({ memberId }: Props) {
  const { data: member } = useGetStaffMemberQuery(memberId);
  const { data: machineGroupsData } = useGetMachineGroupsQuery({});
  const weeks = member?.schedule_weeks || [];

  const machineGroups: any[] = machineGroupsData?.results || machineGroupsData || [];

  const machineGroupMap = useMemo(() => {
    const m = new Map<number, string>();
    machineGroups.forEach((g: any) => m.set(g.id, g.name));
    return m;
  }, [machineGroups]);

  const renderMachineTags = (groupIds: number[], machineIds: number[]) => (
    <>
      {groupIds.map((gid: number) => (
        <Tag key={`g-${gid}`} color="geekblue">{machineGroupMap.get(gid) || `Group #${gid}`}</Tag>
      ))}
      {machineIds.length > 0 && (
        <Tag color="blue">{machineIds.length} machine(s)</Tag>
      )}
    </>
  );
  const weekOptions = weeks.map((w: any) => ({
    value: w.id,
    label: `Week ${w.week_type}`,
  }));

  const [viewStart, setViewStart] = useState(() => getSunday(dayjs()));
  const viewEnd = viewStart.add(12, "week");

  const { data: deployedData } = useGetStaffDeployedWeeksQuery({
    staff_member: memberId,
    week_start_date__gte: viewStart.format("YYYY-MM-DD"),
    week_start_date__lte: viewEnd.format("YYYY-MM-DD"),
  });
  const deployed: any[] = deployedData?.results || deployedData || [];

  const [bulkDeploy] = useBulkDeployWeeksMutation();
  const [deleteDeployed] = useDeleteStaffDeployedWeekMutation();

  const { data: vacationsData } = useGetStaffVacationsQuery(memberId);
  const [createVacation] = useCreateStaffVacationMutation();
  const [updateVacation] = useUpdateStaffVacationMutation();
  const [deleteVacation] = useDeleteStaffVacationMutation();
  const vacations: any[] = vacationsData || [];

  const { data: overtimesData } = useGetStaffOvertimesQuery(memberId);
  const [createOvertime] = useCreateStaffOvertimeMutation();
  const [updateOvertime] = useUpdateStaffOvertimeMutation();
  const [deleteOvertime] = useDeleteStaffOvertimeMutation();
  const overtimes: any[] = overtimesData || [];

  const [excludeShift] = useExcludeShiftFromDeployedWeekMutation();

  const { data: dayTemplatesData } = useGetShiftDayTemplatesQuery({ staff_member: memberId });
  const dayTemplates: any[] = dayTemplatesData?.results || dayTemplatesData || [];
  const [setDayOverride] = useSetDayOverrideMutation();
  const [clearDayOverride] = useClearDayOverrideMutation();

  const [selectedWeeks, setSelectedWeeks] = useState<string[]>([]);
  const [deployTemplate, setDeployTemplate] = useState<number | null>(null);
  const [expandedWeekDate, setExpandedWeekDate] = useState<string | null>(null);
  const [dayOverrideModalDay, setDayOverrideModalDay] = useState<number | null>(null);

  const [vacModalOpen, setVacModalOpen] = useState(false);
  const [vacFormOpen, setVacFormOpen] = useState(false);
  const [editingVacId, setEditingVacId] = useState<number | null>(null);
  const [vacForm] = Form.useForm();

  const [otModalOpen, setOtModalOpen] = useState(false);
  const [otFormOpen, setOtFormOpen] = useState(false);
  const [editingOtId, setEditingOtId] = useState<number | null>(null);
  const [otForm] = Form.useForm();

  const { data: expandedDetail } = useGetStaffDeployedWeekQuery(
    deployed.find((d: any) => d.week_start_date === expandedWeekDate)?.id,
    { skip: !expandedWeekDate || !deployed.find((d: any) => d.week_start_date === expandedWeekDate) }
  );

  const calendarWeeks = useMemo(() => {
    const result = [];
    let current = viewStart;
    for (let i = 0; i < 12; i++) {
      const dateStr = current.format("YYYY-MM-DD");
      const dep = deployed.find((d: any) => d.week_start_date === dateStr);
      result.push({
        date: dateStr,
        display: current.format("MMM D"),
        endDisplay: current.add(6, "day").format("MMM D"),
        deployed: dep,
      });
      current = current.add(1, "week");
    }
    return result;
  }, [viewStart, deployed]);

  const handleDeploy = async () => {
    if (!deployTemplate || selectedWeeks.length === 0) {
      message.warning("Select a template and at least one week");
      return;
    }
    try {
      await bulkDeploy({
        staff_member: memberId,
        schedule_week: deployTemplate,
        week_start_dates: selectedWeeks,
        is_cleared: false,
      }).unwrap();
      message.success(`Deployed to ${selectedWeeks.length} week(s)`);
      setSelectedWeeks([]);
    } catch (err: any) {
      message.error(err?.data?.detail || "Deploy failed");
    }
  };

  const handleClear = async () => {
    if (selectedWeeks.length === 0) return;
    try {
      await bulkDeploy({
        staff_member: memberId,
        schedule_week: null,
        week_start_dates: selectedWeeks,
        is_cleared: true,
      }).unwrap();
      message.success(`Cleared ${selectedWeeks.length} week(s)`);
      setSelectedWeeks([]);
    } catch (err: any) {
      message.error(err?.data?.detail || "Clear failed");
    }
  };

  const handleRemove = async (depId: number) => {
    try {
      await deleteDeployed(depId).unwrap();
      message.success("Deployment removed");
    } catch (err: any) {
      message.error("Failed to remove");
    }
  };

  const toggleWeek = (dateStr: string) => {
    setSelectedWeeks((prev) =>
      prev.includes(dateStr) ? prev.filter((d) => d !== dateStr) : [...prev, dateStr]
    );
  };

  const selectAll = () => {
    setSelectedWeeks(calendarWeeks.map((w) => w.date));
  };

  const handleOpenVacCreate = () => {
    setEditingVacId(null);
    vacForm.resetFields();
    setVacFormOpen(true);
  };

  const handleOpenVacEdit = (vac: any) => {
    setEditingVacId(vac.id);
    vacForm.setFieldsValue({
      dates: [dayjs(vac.start_datetime), dayjs(vac.end_datetime)],
      note: vac.note || "",
    });
    setVacFormOpen(true);
  };

  const handleVacSubmit = async (values: any) => {
    const [start, end] = values.dates;
    const payload = {
      start_datetime: toFacilityUTC(start),
      end_datetime: toFacilityUTC(end),
      note: values.note || "",
    };
    try {
      if (editingVacId) {
        await updateVacation({ id: editingVacId, ...payload }).unwrap();
        message.success("Vacation updated");
      } else {
        await createVacation({ staffId: memberId, staff_member: memberId, ...payload }).unwrap();
        message.success("Vacation created");
      }
      setVacFormOpen(false);
      vacForm.resetFields();
      setEditingVacId(null);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error saving vacation");
    }
  };

  const handleVacDelete = async (id: number) => {
    try {
      await deleteVacation(id).unwrap();
      message.success("Vacation deleted");
    } catch {
      message.error("Failed to delete vacation");
    }
  };

  const handleOpenOtCreate = () => {
    setEditingOtId(null);
    otForm.resetFields();
    setOtFormOpen(true);
  };

  const handleOpenOtEdit = (ot: any) => {
    setEditingOtId(ot.id);
    otForm.setFieldsValue({
      dates: [dayjs(ot.start_datetime), dayjs(ot.end_datetime)],
      note: ot.note || "",
      block: ot.block || null,
    });
    setOtFormOpen(true);
  };

  const handleOtSubmit = async (values: any) => {
    const [start, end] = values.dates;
    const payload: any = {
      start_datetime: start.toISOString(),
      end_datetime: end.toISOString(),
      note: values.note || "",
      block: values.block || null,
    };
    try {
      if (editingOtId) {
        await updateOvertime({ id: editingOtId, ...payload }).unwrap();
        message.success("Overtime updated");
      } else {
        await createOvertime({ staffId: memberId, staff_member: memberId, ...payload }).unwrap();
        message.success("Overtime created");
      }
      setOtFormOpen(false);
      otForm.resetFields();
      setEditingOtId(null);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error saving overtime");
    }
  };

  const handleOtDelete = async (id: number) => {
    try {
      await deleteOvertime(id).unwrap();
      message.success("Overtime deleted");
    } catch {
      message.error("Failed to delete overtime");
    }
  };

  const handleExcludeShift = async (deployedWeekId: number, shiftId: number) => {
    try {
      await excludeShift({ id: deployedWeekId, shift_id: shiftId }).unwrap();
      message.success("Shift excluded from this week");
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to exclude shift");
    }
  };

  const blockOptions = useMemo(() => {
    const opts: { value: number; label: string }[] = [];
    for (const sw of weeks) {
      for (const shift of sw.shifts || []) {
        for (const blk of shift.blocks || []) {
          const slotsLabel = (blk.slots || []).map((s: any) => s.task_name).join(", ");
          opts.push({
            value: blk.id,
            label: `${blk.name}${slotsLabel ? ` (${slotsLabel})` : ""}`,
          });
        }
      }
    }
    return opts;
  }, [weeks]);

  const otColumns = [
    {
      title: "Start",
      dataIndex: "start_datetime",
      key: "start",
      render: (v: string) => dayjs(v).format("MMM D, YYYY h:mm A"),
    },
    {
      title: "End",
      dataIndex: "end_datetime",
      key: "end",
      render: (v: string) => dayjs(v).format("MMM D, YYYY h:mm A"),
    },
    {
      title: "Block",
      dataIndex: "block_name",
      key: "block_name",
      render: (v: string | null) => v || <span className="text-gray-400">None</span>,
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenOtEdit(record)}
          />
          <Popconfirm title="Delete this overtime?" onConfirm={() => handleOtDelete(record.id)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const vacColumns = [
    {
      title: "Start",
      dataIndex: "start_datetime",
      key: "start",
      render: (v: string) => formatDateTime(v, "MMM D, YYYY h:mm A"),
    },
    {
      title: "End",
      dataIndex: "end_datetime",
      key: "end",
      render: (v: string) => formatDateTime(v, "MMM D, YYYY h:mm A"),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenVacEdit(record)}
          />
          <Popconfirm title="Delete this vacation?" onConfirm={() => handleVacDelete(record.id)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Navigation */}
      <Card size="small" className="mb-4">
        <div className="flex items-center justify-between">
          <Space>
            <Button
              icon={<LeftOutlined />}
              onClick={() => setViewStart(viewStart.subtract(4, "week"))}
            />
            <span className="font-medium">
              {viewStart.format("MMM D, YYYY")} – {viewEnd.subtract(1, "day").format("MMM D, YYYY")}
            </span>
            <Button
              icon={<RightOutlined />}
              onClick={() => setViewStart(viewStart.add(4, "week"))}
            />
            <Button onClick={() => setViewStart(getSunday(dayjs()))}>Today</Button>
            <Button onClick={() => setVacModalOpen(true)}>Manage Vacations</Button>
            <Button onClick={() => setOtModalOpen(true)}>Manage Overtime</Button>
          </Space>
        </div>
      </Card>

      {/* Deploy controls */}
      <Card size="small" className="mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select
            value={deployTemplate}
            onChange={setDeployTemplate}
            options={weekOptions}
            placeholder="Select template..."
            style={{ width: 160 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            onClick={handleDeploy}
            disabled={!deployTemplate || selectedWeeks.length === 0}
          >
            Deploy to {selectedWeeks.length} week(s)
          </Button>
          <Popconfirm title="Clear selected weeks?" onConfirm={handleClear}>
            <Button danger disabled={selectedWeeks.length === 0}>
              Clear {selectedWeeks.length} week(s)
            </Button>
          </Popconfirm>
          <Button type="link" onClick={selectAll}>
            Select all
          </Button>
          <Button type="link" onClick={() => setSelectedWeeks([])}>
            Deselect all
          </Button>
        </div>
      </Card>

      {/* Calendar grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
        {calendarWeeks.map((w) => {
          const isSelected = selectedWeeks.includes(w.date);
          const dep = w.deployed;
          const isExpanded = expandedWeekDate === w.date;

          return (
            <Card
              key={w.date}
              size="small"
              className={`cursor-pointer transition-all ${
                isSelected ? "border-blue-500 border-2" : ""
              } ${
                dep && !dep.is_cleared
                  ? dep.schedule_week_type === "A"
                    ? "bg-blue-50"
                    : "bg-purple-50"
                  : dep?.is_cleared
                    ? "bg-red-50"
                    : "bg-gray-50"
              }`}
              onClick={() => toggleWeek(w.date)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <Checkbox checked={isSelected} />
                  <span className="font-medium text-sm ml-2">{w.display} – {w.endDisplay}</span>
                  {dep && !dep.is_cleared && dep.shift_total_hours && (
                    <span className="text-xs text-gray-500 ml-2">({dep.shift_total_hours})</span>
                  )}
                </div>
                {dep && !dep.is_cleared && (
                  <Tag color={dep.schedule_week_type === "A" ? "blue" : "purple"}>
                    Week {dep.schedule_week_type}
                  </Tag>
                )}
                {dep?.is_cleared && <Tag color="red">Cleared</Tag>}
                {!dep && <Tag>Unassigned</Tag>}
              </div>

              {dep && !dep.is_cleared && (
                <>
                  {(dep.shift_lines || []).length > 0 && (
                    <div className="text-xs text-gray-500 mt-1 leading-relaxed">
                      {dep.shift_lines.map((line: string, i: number) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                  {dep.vacation_summary && (
                    <Tag color="orange" className="mt-1">{dep.vacation_summary}</Tag>
                  )}
                  {dep.overtime_summary && (
                    <Tag color="green" className="mt-1">{dep.overtime_summary}</Tag>
                  )}
                </>
              )}
              {dep && (
                <div className="mt-2 flex gap-1">
                  {!dep.is_cleared && (
                    <Button
                      type="link"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedWeekDate(isExpanded ? null : w.date);
                      }}
                    >
                      {isExpanded ? "Hide details" : "View details"}
                    </Button>
                  )}
                  <Popconfirm
                    title="Remove this deployment?"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleRemove(dep.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Expanded week detail */}
      {expandedWeekDate && expandedDetail?.schedule_detail && (
        <Card
          title={`Schedule for week of ${dayjs(expandedWeekDate).format("MMM D, YYYY")}`}
          className="mb-4"
        >
          {/* Day overrides row */}
          <div className="mb-4">
            <div className="text-sm font-semibold mb-2">Day Overrides</div>
            <Space size={[8, 8]} wrap>
              {DAY_LABELS.map((label, idx) => {
                const override = (expandedDetail.day_overrides || []).find(
                  (o: any) => o.day_index === idx
                );
                const hasOverride = !!override;
                return (
                  <Tooltip
                    key={idx}
                    title={
                      hasOverride
                        ? override.is_cleared
                          ? "Cleared (no work)"
                          : `Override: ${override.override_template_name}`
                        : "Using template default"
                    }
                  >
                    <Button
                      size="small"
                      type={hasOverride ? "primary" : "default"}
                      danger={override?.is_cleared}
                      onClick={() => setDayOverrideModalDay(idx)}
                    >
                      {label}
                      {hasOverride && " *"}
                    </Button>
                  </Tooltip>
                );
              })}
            </Space>
          </div>

          {/* Day override modal */}
          <Modal
            title={`Override ${DAY_LABELS_FULL[dayOverrideModalDay ?? 0]}`}
            open={dayOverrideModalDay !== null}
            onCancel={() => setDayOverrideModalDay(null)}
            footer={null}
            width={400}
          >
            <Space direction="vertical" className="w-full">
              <Button
                block
                onClick={async () => {
                  if (dayOverrideModalDay === null) return;
                  try {
                    await clearDayOverride({ id: expandedDetail.id, day_index: dayOverrideModalDay }).unwrap();
                    message.success("Reverted to template default");
                    setDayOverrideModalDay(null);
                  } catch (err: any) {
                    message.error(err?.data?.detail || "Error clearing override");
                  }
                }}
              >
                Use Template Default
              </Button>
              <Button
                block
                danger
                onClick={async () => {
                  if (dayOverrideModalDay === null) return;
                  try {
                    await setDayOverride({ id: expandedDetail.id, day_index: dayOverrideModalDay, is_cleared: true }).unwrap();
                    message.success("Day cleared");
                    setDayOverrideModalDay(null);
                  } catch (err: any) {
                    message.error(err?.data?.detail || "Error setting override");
                  }
                }}
              >
                Clear Day (No Work)
              </Button>
              {dayTemplates.length > 0 && (
                <>
                  <div className="text-sm text-gray-500 mt-2">Apply a day template:</div>
                  {dayTemplates.map((tpl: any) => (
                    <Button
                      key={tpl.id}
                      block
                      onClick={async () => {
                        if (dayOverrideModalDay === null) return;
                        try {
                          await setDayOverride({
                            id: expandedDetail.id,
                            day_index: dayOverrideModalDay,
                            override_template: tpl.id,
                          }).unwrap();
                          message.success(`Applied "${tpl.name}" to ${DAY_LABELS_FULL[dayOverrideModalDay]}`);
                          setDayOverrideModalDay(null);
                        } catch (err: any) {
                          message.error(err?.data?.detail || "Error setting override");
                        }
                      }}
                    >
                      {tpl.name}
                    </Button>
                  ))}
                </>
              )}
            </Space>
          </Modal>

          {(expandedDetail.schedule_detail.shifts || []).length === 0 ? (
            <Empty description="No shifts in this template" />
          ) : (
            <Collapse>
              {expandedDetail.schedule_detail.shifts.map((shift: any) => (
                <Collapse.Panel
                  key={shift.id}
                  header={
                    <div className="flex items-center justify-between w-full">
                      <Space size={[4, 4]} wrap>
                        <span>
                          {DAY_LABELS_FULL[shift.start_day]} {String(shift.start_time).slice(0, 5)} –{" "}
                          {DAY_LABELS_FULL[shift.end_day]} {String(shift.end_time).slice(0, 5)}
                        </span>
                        {renderMachineTags(
                          shift.attended_machine_groups_shift_level || [],
                          shift.attended_machines_shift_level || [],
                        )}
                      </Space>
                      <Popconfirm
                        title="Remove this shift from this week?"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          handleExcludeShift(expandedDetail.id, shift.id);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </div>
                  }
                >
                  {(shift.blocks || []).map((block: any) => (
                    <Card key={block.id} size="small" className="mb-2" title={block.name}>
                      <div className="text-xs text-gray-500 mb-2">
                        <span>
                          {DAY_LABELS_FULL[block.start_day]} {String(block.start_time).slice(0, 5)} –{" "}
                          {DAY_LABELS_FULL[block.end_day]} {String(block.end_time).slice(0, 5)}
                        </span>
                        <span className="ml-2">
                          {block.use_shift_machines
                            ? renderMachineTags(
                                shift.attended_machine_groups_shift_level || [],
                                shift.attended_machines_shift_level || [],
                              )
                            : renderMachineTags(
                                block.attended_machine_groups || [],
                                block.attended_machines || [],
                              )}
                        </span>
                      </div>
                      {(block.slots || []).length > 0 ? (
                        <Table
                          dataSource={block.slots}
                          rowKey="id"
                          size="small"
                          pagination={false}
                          columns={[
                            { title: "Task", dataIndex: "task_name", key: "task_name" },
                            { title: "ID", dataIndex: "task_task_id", key: "task_task_id", width: 60 },
                            { title: "Slots", dataIndex: "count", key: "count", width: 60 },
                            {
                              title: "Distribution",
                              dataIndex: "distribution",
                              key: "distribution",
                              render: (v: string) => (
                                <Tag color={v === "front" ? "blue" : v === "back" ? "orange" : "green"}>
                                  {v === "front" ? "Front" : v === "back" ? "Back" : "Distributed"}
                                </Tag>
                              ),
                            },
                          ]}
                        />
                      ) : (
                        <p className="text-gray-400 italic text-xs">No slots</p>
                      )}
                    </Card>
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          )}

          {/* Overtime entries for this week */}
          {(() => {
            const weekStart = dayjs(expandedWeekDate);
            const weekEnd = weekStart.add(6, "day").endOf("day");
            const weekOvertimes = overtimes.filter((ot: any) => {
              const otStart = dayjs(ot.start_datetime);
              const otEnd = dayjs(ot.end_datetime);
              return otStart.isBefore(weekEnd) && otEnd.isAfter(weekStart);
            });
            return (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">Overtime ({weekOvertimes.length})</span>
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleOpenOtCreate}
                  >
                    Add Overtime
                  </Button>
                </div>
                {weekOvertimes.length === 0 ? (
                  <p className="text-gray-400 italic text-xs">No overtime this week</p>
                ) : (
                  <Table
                    dataSource={weekOvertimes}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: "Date / Time",
                        key: "range",
                        render: (_: any, record: any) =>
                          `${dayjs(record.start_datetime).format("ddd MMM D HH:mm")} – ${dayjs(record.end_datetime).format("HH:mm")}`,
                      },
                      {
                        title: "Block",
                        key: "block",
                        width: 200,
                        render: (_: any, record: any) => (
                          <Select
                            size="small"
                            allowClear
                            placeholder="No block"
                            value={record.block || undefined}
                            options={blockOptions}
                            style={{ width: "100%" }}
                            onChange={async (val) => {
                              try {
                                await updateOvertime({ id: record.id, block: val || null });
                                message.success("Block updated");
                              } catch {
                                message.error("Failed to update block");
                              }
                            }}
                          />
                        ),
                      },
                      {
                        title: "Note",
                        dataIndex: "note",
                        key: "note",
                        ellipsis: true,
                      },
                      {
                        title: "",
                        key: "actions",
                        width: 80,
                        render: (_: any, record: any) => (
                          <Space>
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleOpenOtEdit(record)}
                            />
                            <Popconfirm title="Delete?" onConfirm={() => handleOtDelete(record.id)}>
                              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                )}
              </div>
            );
          })()}
        </Card>
      )}

      {/* Vacation Management Modal */}
      <Modal
        title="Manage Vacations"
        open={vacModalOpen}
        onCancel={() => setVacModalOpen(false)}
        footer={null}
        width={700}
      >
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-gray-500">{vacations.length} vacation(s) on file</span>
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={handleOpenVacCreate}>
            Add Vacation
          </Button>
        </div>
        {vacations.length === 0 ? (
          <Empty description="No vacations scheduled" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={vacations}
            columns={vacColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </Modal>

      {/* Add / Edit Vacation Modal */}
      <Modal
        title={editingVacId ? "Edit Vacation" : "Add Vacation"}
        open={vacFormOpen}
        onCancel={() => { setVacFormOpen(false); setEditingVacId(null); }}
        onOk={() => vacForm.submit()}
        width={480}
        destroyOnClose
      >
        <Form form={vacForm} layout="vertical" onFinish={handleVacSubmit}>
          <Form.Item
            name="dates"
            label="Date Range"
            rules={[{ required: true, message: "Select start and end dates" }]}
          >
            <DatePicker.RangePicker
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={2} placeholder="e.g. Family vacation, Appointment" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Overtime Management Modal */}
      <Modal
        title="Manage Overtime"
        open={otModalOpen}
        onCancel={() => setOtModalOpen(false)}
        footer={null}
        width={700}
      >
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-gray-500">{overtimes.length} overtime entry(ies) on file</span>
          <Button icon={<PlusOutlined />} type="primary" size="small" onClick={handleOpenOtCreate}>
            Add Overtime
          </Button>
        </div>
        {overtimes.length === 0 ? (
          <Empty description="No overtime scheduled" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={overtimes}
            columns={otColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </Modal>

      {/* Add / Edit Overtime Modal */}
      <Modal
        title={editingOtId ? "Edit Overtime" : "Add Overtime"}
        open={otFormOpen}
        onCancel={() => { setOtFormOpen(false); setEditingOtId(null); }}
        onOk={() => otForm.submit()}
        width={480}
        destroyOnClose
      >
        <Form form={otForm} layout="vertical" onFinish={handleOtSubmit}>
          <Form.Item
            name="dates"
            label="Date Range"
            rules={[{ required: true, message: "Select start and end dates" }]}
          >
            <DatePicker.RangePicker
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="block" label="Block (task slots)">
            <Select
              allowClear
              placeholder="No block — time only"
              options={blockOptions}
            />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={2} placeholder="e.g. Weekend coverage, Rush order" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
